<template>
  <label :class="['vue-js-switch', { toggled: isChecked, disabled: disabled }]">
    <input
      class="v-switch-input"
      type="checkbox"
      :name="name"
      :disabled="disabled"
      :checked="isChecked"
      @change.stop="toggle"
    />
    <div class="v-switch-core" :style="coreStyle">
      <div class="v-switch-button" :style="buttonStyle"></div>
    </div>
    <span v-if="labels && isChecked" class="v-switch-label v-left" :style="labelStyle">{{ labelChecked }}</span>
    <span v-else-if="labels" class="v-switch-label v-right" :style="labelStyle">{{ labelUnchecked }}</span>
  </label>
</template>

<script>
export default {
  name: 'ToggleButton',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Boolean,
      default: undefined
    },
    name: {
      type: String,
      default: undefined
    },
    disabled: {
      type: Boolean,
      default: false
    },
    speed: {
      type: Number,
      default: 300
    },
    color: {
      type: [String, Object],
      default: () => ({})
    },
    switchColor: {
      type: [String, Object],
      default: undefined
    },
    labels: {
      type: [Boolean, Object],
      default: false
    },
    height: {
      type: Number,
      default: 22
    },
    width: {
      type: Number,
      default: 50
    },
    margin: {
      type: Number,
      default: 3
    },
    fontSize: {
      type: Number,
      default: undefined
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    const initValue = this.modelValue !== undefined ? this.modelValue : this.value;
    return {
      toggled: initValue
    };
  },
  computed: {
    isChecked() {
      return this.toggled;
    },
    colorChecked() {
      if (!this.color || typeof this.color === 'string') return this.color || '#75c791';
      return this.color.checked || '#75c791';
    },
    colorUnchecked() {
      if (!this.color || typeof this.color === 'string') return '#bfcbd9';
      return this.color.unchecked || '#bfcbd9';
    },
    colorCurrent() {
      return this.toggled ? this.colorChecked : this.colorUnchecked;
    },
    buttonRadius() {
      return this.height - this.margin * 2;
    },
    distance() {
      return this.width - this.height + this.margin;
    },
    coreStyle() {
      return {
        width: this.width + 'px',
        height: this.height + 'px',
        backgroundColor: this.colorCurrent,
        borderRadius: Math.round(this.height / 2) + 'px'
      };
    },
    buttonStyle() {
      const x = this.toggled ? this.distance + 'px' : this.margin + 'px';
      const y = this.margin + 'px';
      return {
        width: this.buttonRadius + 'px',
        height: this.buttonRadius + 'px',
        transition: `transform ${this.speed}ms`,
        transform: `translate3d(${x}, ${y}, 0px)`
      };
    },
    labelStyle() {
      return {
        lineHeight: this.height + 'px',
        fontSize: this.fontSize ? this.fontSize + 'px' : null
      };
    },
    labelChecked() {
      if (!this.labels || this.labels === true) return 'on';
      return this.labels.checked || 'on';
    },
    labelUnchecked() {
      if (!this.labels || this.labels === true) return 'off';
      return this.labels.unchecked || 'off';
    }
  },
  methods: {
    toggle(event) {
      const next = !this.toggled;
      this.toggled = next;
      this.$emit('update:modelValue', next);
      this.$emit('change', { value: next, srcEvent: event });
    }
  },
  watch: {
    value(val) {
      this.toggled = val;
    },
    modelValue(val) {
      if (val !== undefined) this.toggled = val;
    }
  }
};
</script>

<style scoped>
.vue-js-switch {
  display: inline-block;
  position: relative;
  vertical-align: middle;
  user-select: none;
  font-size: 10px;
  cursor: pointer;
}

.vue-js-switch .v-switch-input {
  opacity: 0;
  position: absolute;
  width: 1px;
  height: 1px;
}

.vue-js-switch .v-switch-label {
  position: absolute;
  top: 0;
  font-weight: 600;
  color: #fff;
  z-index: 1;
}

.vue-js-switch .v-switch-label.v-left {
  left: 10px;
}

.vue-js-switch .v-switch-label.v-right {
  right: 10px;
}

.vue-js-switch .v-switch-core {
  display: block;
  position: relative;
  box-sizing: border-box;
  outline: 0;
  margin: 0;
  transition: border-color 0.3s, background-color 0.3s;
  user-select: none;
}

.vue-js-switch .v-switch-core .v-switch-button {
  display: block;
  position: absolute;
  overflow: hidden;
  top: 0;
  left: 0;
  border-radius: 100%;
  background-color: #fff;
  z-index: 2;
}

.vue-js-switch.disabled {
  pointer-events: none;
  opacity: 0.6;
}
</style>
